/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
/** biome-ignore-all lint/style/useNamingConvention: allow snake case */

import cx from 'classnames';
import {lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {EVENT_STATUS_DRAFT} from '~/common/utils/event_constants';
import {trackUpsell} from '~/common/utils/upsellTracking';
import {subscribeToUserStateChanges} from '~/common/utils/userStateSync';
import {isIn3AcrossABTest} from '~/GalleryPB/experiments';
import {FilterPreviewer} from '~/GalleryPB/FilterPreviewer';
import {FiltersDrawer} from '~/GalleryPB/FiltersDrawer';
import {GalleryResults} from '~/GalleryPB/GalleryResults';
import {Header} from '~/GalleryPB/Header';
import {history} from '~/GalleryPB/history';
import {useTemplateHelpers} from '~/GalleryPB/hooks/useTemplateHelpers';
import {useUyoOrDyo} from '~/GalleryPB/hooks/useUyoOrDyo';
import {NoFavorites} from '~/GalleryPB/NoFavorites';
import {ResultsHeader} from '~/GalleryPB/ResultsHeader';
import {SET_ACTIVE_FILTERS} from '~/GalleryPB/reducers/filters';
import {selectActiveFilters} from '~/GalleryPB/reducers/filters/selectors';
import {
  RESET_FILTERS,
  SET_PREVIOUS_CURRENT_PAGE,
  UPDATE_ACTIVE_FILTERS,
} from '~/GalleryPB/reducers/galleryData';
import {SEODescriptionBlock} from '~/GalleryPB/SEODescriptionBlock';
import {SeoHeaderWithBreadcrumb} from '~/GalleryPB/SeoHeaderWithBreadcrumb';
import {SubCategories} from '~/GalleryPB/SubCategories';
import {defaultTemplatePreviewConfig} from '~/GalleryPB/Template/components/QuickView/default_preview_config';
import {QuickView} from '~/GalleryPB/Template/components/QuickView/QuickView';
import {buildHref, processPageAttributes, showSeoHeader} from '~/GalleryPB/utils';
import {Divider} from '~library/atoms/Divider';
import {useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {Pagination} from '~library/molecules/Pagination';
import {userLoggedIn} from '~library/organisms/LoginModal/reducer/actions';
import {LoginModalWrapper} from '../../../../ecl/organisms/LoginModal/LoginModalWrapper';

const PreviewPhotoUpload = lazy(() =>
  import('~/GalleryPB/components/PreviewPhotoUpload/PreviewPhotoUpload').then((module) => ({
    default: module.PreviewPhotoUpload,
  }))
);

export const GalleryRoot = () => {
  const dispatch = useDispatch();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [isWaitingForSearchResults, setIsWaitingForSearchResults] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const pageDisplayName = useSelector((state) => state.galleryData.pageDisplayName);
  const activeFilters = useSelector(selectActiveFilters);
  const [changedFilters, setChangedFilters] = useState([]);
  const previousCurrentPage = useSelector((state) => state.galleryData.previousCurrentPage);
  const [hideQuickViewCtas, setHideQuickViewCtas] = useState(false);
  const {isInUyoOrDyo} = useUyoOrDyo();
  const [templateToQuickView, setTemplateToQuickView] = useState(null);
  const [templateIdToQuickView, setTemplateIdToQuickView] = useState(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const isMediumAndUp = useIsMediumAndUp();
  const {findTemplateById, checkIfQuickViewEnabled} = useTemplateHelpers();

  // Subscribe to user state changes from other stores
  useEffect(() => {
    return subscribeToUserStateChanges((userData) => {
      if (userData?.userId) {
        dispatch(
          userLoggedIn(
            userData.userId,
            userData.userName,
            userData.subscriptionPlanName,
            userData.email,
            userData.avatarDisk,
            userData.imgUrl,
            userData.initials,
            userData.firstName
          )
        );
      }
    });
  }, [dispatch]);

  const isLargishAndUp = useIsLargishAndUp();
  const {
    templates,
    filterSections: filters,
    categoryId,
    breadcrumbs,
    currentPage,
    pageCount,
    numTemplates,
    relatives,
    replaced,
    tracking,
  } = useSelector((state) => state.galleryData);

  const {favorites} = useSelector((state) => state.favorites);

  const store = useStore();

  const urlPieces = useRef(window.location.pathname.split('/')).current;

  const galleryCategoryBasePath = window.cards_page ? '/cards' : '/invites';

  const in3AcrossABTest = isIn3AcrossABTest();

  const templatesPerRowOnDesktop = in3AcrossABTest ? 3 : 4;

  document.documentElement.style.setProperty(
    '--gallery-templates-per-row-desktop',
    templatesPerRowOnDesktop.toString()
  );

  /**
   * Determines if a given pathname is a gallery category path.
   *
   * This function checks if the input pathname starts with '/invites' or '/cards',
   * implying that the path is for a gallery category.
   *
   * @param {string} pathname - The pathname to check.
   * @returns {boolean} Returns true if the pathname starts with '/invites' or '/cards', else returns false.
   */
  const isGalleryCategoryPathname = useCallback(
    (pathname) => pathname.startsWith('/invites') || pathname.startsWith('/cards'),
    []
  );

  /**
   * Pushes a new path to the history object, updating the URL and history stack.
   *
   * @param {URLSearchParams} searchParams - The URL search parameters.
   * @param {Object} activeFiltersToInit - The currently active filters for the data.
   *
   * Modifies the URL and history stack by pushing a new path derived from current pathname and base path.
   */
  const pushPathToHistory = useCallback(
    (searchParams, activeFiltersToInit) => {
      let {pathname} = window.location;
      if (isGalleryCategoryPathname(pathname)) {
        const hierarchicalCategoriesPath = window.location.pathname
          .split('/')
          .slice(2, -1)
          .join('/');
        pathname = `${galleryCategoryBasePath}/${
          hierarchicalCategoriesPath ? `${hierarchicalCategoriesPath}/` : ''
        }`;
      }
      history.push({
        pathname,
        hash: history.location.hash,
        search: searchParams.toString(),
        state: {
          activeFilters: activeFiltersToInit,
        },
      });
    },
    [galleryCategoryBasePath, isGalleryCategoryPathname]
  );

  /**
   * Submit filters and get new gallery data
   * @param filtersToSubmit
   * @param page
   * @returns {Promise<{
   *    breadcrumbs?: object[],
   *    categoryId?: string,
   *    displayName?: string,
   *    filterSections: object[],
   *    freeTemplates: object[],
   *    page: number,
   *    pageCount: number,
   *    premiumTemplates: object[],
   *    searchText?: string,
   *    total: number,
   *    cards_page?: boolean,
   *    category_name?: string,
   *    category_id?: string,
   *    query?: string,
   *    is_favorites?: boolean,
   *    seo_description_copy?: string,
   *    seo_display_name?: string,
   *    top_of_page_copy?: string,
   *    top_of_page_img_desk?: string,
   *    top_of_page_img_mob?: string,
   *    meta_title?: string,
   *    meta_description?: string,
   *    noindex_url?: boolean,
   * }>}
   */
  const refreshGalleryData = useCallback(
    async (filtersToSubmit = [], page = 1) => {
      // Convert list of filters to submit into a filter object for the API request
      const filterData = {};
      filtersToSubmit.forEach((f) => {
        if (Object.keys(filterData).includes(f.filterSection)) {
          filterData[f.filterSection].push(f.filterName);
        } else {
          filterData[f.filterSection] = [f.filterName];
        }
      });

      let response;
      // Get category
      const {category_id: categoryID} = window;
      if (categoryID) {
        const requestBody = {
          filters: Object.keys(filterData).length > 0 ? filterData : undefined,
          page,
          category_id: categoryID,
          match_to: params.get('match_to'),
          invitation_type: window.cards_page ? 'card' : 'invitation',
        };
        // Submit request and return response body
        response = await fetch(`/ajax/gallery/query`, {
          method: 'POST',
          body: JSON.stringify(requestBody),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': evite.cookie('csrftoken'),
          },
        });
      } else {
        const searchChunk = urlPieces.find((p) => p === 'search');
        const searchParams = new URLSearchParams(window.location.search);
        const searchTerm = searchParams.get('query');
        if (searchChunk && searchTerm) {
          response = await fetch(`/ajax/gallery/query`, {
            method: 'POST',
            body: JSON.stringify({
              filters: Object.keys(filterData).length > 0 ? filterData : undefined,
              page,
              search_term: searchTerm,
              invitation_type: searchParams.get('filter'),
            }),
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': evite.cookie('csrftoken'),
            },
          });
        } else if (urlPieces.includes('favorites')) {
          // My Favorites page
          response = await fetch(`/ajax/gallery/query`, {
            method: 'POST',
            body: JSON.stringify({
              category_id: 'favorites',
            }),
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': evite.cookie('csrftoken'),
            },
          });
        } else {
          // Fall back to showing featured items
          const requestBody = {
            filters: Object.keys(filterData).length > 0 ? filterData : undefined,
            page,
            category_id: window.cards_page ? 'featured_cards' : 'featured',
            invitation_type: window.cards_page ? 'card' : 'invitation',
          };
          // Submit request and return response body
          response = await fetch(`/ajax/gallery/query`, {
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': evite.cookie('csrftoken'),
            },
          });
        }
      }
      const result = await response.json();
      processPageAttributes(result);
      return result;
    },
    [params, urlPieces]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    // Get initial gallery items, filters, breadcrumbs, and page data
    const url = new URL(window.location.href);
    pushPathToHistory(url.searchParams, activeFilters);
  }, []);

  const buildHeaderFilters = (filterSections) => {
    const headerFilters = [];
    Object.entries(filterSections).forEach(([fsKey, fsVal]) => {
      Object.entries(fsVal.filters).forEach(([filterKey, filter]) => {
        if (filter.on) {
          headerFilters.push({
            sectionId: fsKey,
            filterName: filterKey,
            displayName: filter.display_name,
          });
        }
      });
    });
    return headerFilters;
  };

  const previewedFilters = buildHeaderFilters(filters);

  /**
   * Handle the user pressing Cancel
   * @returns {Promise<void>}
   */
  const handleCancelClick = useCallback(async () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('active_filter');

    activeFilters.forEach((f) => {
      searchParams.append('active_filter', `${f.filterSection},${f.filterName}`);
    });

    // Request updated gallery data with previous filters and revert in Redux and state values
    searchParams.set('page', previousCurrentPage.toString());
    pushPathToHistory(searchParams, activeFilters);
    window.scrollTo(0, 0);
    setIsWaitingForSearchResults(true);
    const apiResponse = await refreshGalleryData(activeFilters, previousCurrentPage);
    changedFilters.forEach((cf) => {
      store.dispatch({
        type: UPDATE_ACTIVE_FILTERS,
        payload: {
          sectionName: cf.filterSection,
          filterName: cf.filterName,
          apiResponse,
        },
      });
    });

    setIsWaitingForSearchResults(false);
    setChangedFilters([]);
    setIsDrawerOpen(false);
    document.body.classList.remove('no-scroll');
  }, [
    activeFilters,
    changedFilters,
    previousCurrentPage,
    pushPathToHistory,
    refreshGalleryData,
    store,
  ]);

  /**
   * Reset all filters
   */
  const resetFilters = useCallback(
    async (skipPathPush = false) => {
      setIsWaitingForSearchResults(true);
      const apiResponse = await refreshGalleryData([], 1);
      setIsWaitingForSearchResults(false);
      store.dispatch({
        type: RESET_FILTERS,
        payload: {apiResponse},
      });

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('active_filter');
      if (searchParams.get('page'))
        // reset to page 1 if paginated
        searchParams.set('page', '1');
      if (!skipPathPush) pushPathToHistory(searchParams, []);

      window.scrollTo(0, 0);
      store.dispatch({
        type: SET_ACTIVE_FILTERS,
        payload: [],
      });
      setChangedFilters([]);
      store.dispatch({
        type: SET_PREVIOUS_CURRENT_PAGE,
        payload: 1,
      });

      return apiResponse;
    },
    [pushPathToHistory, refreshGalleryData, store]
  );

  /**
   * Handle the user pressing Apply (this is a feel-good button)
   */
  const handleApplyClick = useCallback(
    (changed = changedFilters) => {
      // Update list of active filters
      const updatedActiveFilters = [...activeFilters];
      const activeFilterNames = updatedActiveFilters.map((af) => af.filterName);
      changed.forEach((cf) => {
        if (!activeFilterNames.includes(cf.filterName) && cf.on) {
          updatedActiveFilters.push(cf);
        } else if (!cf.on) {
          const idx = updatedActiveFilters.findIndex((af) => af.filterName === cf.filterName);
          if (idx >= 0) updatedActiveFilters.splice(idx, 1);
        }
      });
      store.dispatch({
        type: SET_ACTIVE_FILTERS,
        payload: updatedActiveFilters,
      });
      setChangedFilters([]);
      setIsDrawerOpen(false);
      document.body.classList.remove('no-scroll');
    },
    [activeFilters, changedFilters, store]
  );

  /**
   * Handle the user toggling a filter
   * @param sectionName the filter section
   * @param filterId the name of the filter
   * @param on whether or not we're turning the filter on or off
   * @param forceApply whether or not to force-apply the filter change all the way through
   * @returns {Promise<void>}
   */
  const handleFilterClick = useCallback(
    async (sectionName, filterId, on, forceApply = false) => {
      // Build list of filters to submit
      const filtersToSubmit = [];
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('active_filter');

      // Update list of changed filters
      const changed = [...changedFilters];
      const idx = changed.findIndex((f) => f.filterName === filterId);
      if (idx >= 0) changed.splice(idx, 1);
      else
        changed.push({
          filterSection: sectionName,
          filterName: filterId,
          on,
        });

      // Include all active filters have not been turned off in changed_filters
      activeFilters.forEach((af) => {
        const activeFilterInChangedFilters = changed.find((cf) => cf.filterName === af.filterName);

        if (!activeFilterInChangedFilters) {
          filtersToSubmit.push(af);
          searchParams.append('active_filter', `${af.filterSection},${af.filterName}`);
        } else if (activeFilterInChangedFilters.on) {
          filtersToSubmit.push(activeFilterInChangedFilters);
          searchParams.append(
            'active_filter',
            `${activeFilterInChangedFilters.filterSection},${activeFilterInChangedFilters.filterName}`
          );
        }
      });

      // Include all changed filters that are set to "on"
      changed
        .filter((cf) => cf.on)
        .forEach((cf) => {
          const changedFilterInFiltersToSubmit = filtersToSubmit.find(
            (f) => f.filterName === cf.filterName
          );
          if (!changedFilterInFiltersToSubmit) {
            filtersToSubmit.push(cf);
            searchParams.append('active_filter', `${cf.filterSection},${cf.filterName}`);
          }
        });
      if (searchParams.get('page'))
        // reset to page 1 if paginated
        searchParams.set('page', '1');
      pushPathToHistory(searchParams, filtersToSubmit);
      window.scrollTo(0, 0);

      // Get updated gallery data and update in Redux and state variables
      setIsWaitingForSearchResults(true);
      const apiResponse = await refreshGalleryData(filtersToSubmit, 1);
      store.dispatch({
        type: UPDATE_ACTIVE_FILTERS,
        payload: {
          sectionName,
          filterName: filterId,
          apiResponse,
        },
      });
      setIsWaitingForSearchResults(false);

      if (isLargishAndUp || forceApply) handleApplyClick(changed);
      else setChangedFilters(changed);
    },
    [
      activeFilters,
      changedFilters,
      handleApplyClick,
      isLargishAndUp,
      pushPathToHistory,
      refreshGalleryData,
      store,
    ]
  );

  /**
   * Handle the user pressing the Filters button to open the filter drawer
   */
  const onFilterButtonClick = useCallback(() => {
    setIsDrawerOpen(true);
    document.body.classList.add('no-scroll');
  }, []);

  /**
   * Handle the user clicking on a pagination page
   * @param page the page to take the user to
   */
  const onPaginationPagePress = useCallback(
    async (page) => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('page', page);
      pushPathToHistory(searchParams, activeFilters);
      window.scrollTo(0, 0);
      setIsWaitingForSearchResults(true);
      const apiResponse = await refreshGalleryData(activeFilters, page);
      setIsWaitingForSearchResults(false);
      store.dispatch({
        type: UPDATE_ACTIVE_FILTERS,
        payload: {apiResponse},
      });

      store.dispatch({
        type: SET_PREVIOUS_CURRENT_PAGE,
        payload: 1,
      });
    },
    [activeFilters, pushPathToHistory, refreshGalleryData, store]
  );

  /**
   * Handle the user clicking the left pagination arrow
   */
  const onPaginationLeftArrowPress = useCallback(() => {
    // Note: if we hit an edge case where the previous page would be 0, keep the user at 1
    const page = Math.max(currentPage - 1, 1);
    return onPaginationPagePress(page);
  }, [currentPage, onPaginationPagePress]);

  /**
   * Handle the user clicking the right pagination arrow
   */
  const onPaginationRightArrowPress = useCallback(() => {
    // Note: if we hit an edge case where the next page would be beyond the page count, keep the user at the last page
    const page = Math.min(currentPage + 1, pageCount);
    return onPaginationPagePress(page);
  }, [currentPage, onPaginationPagePress, pageCount]);

  const onClickQuickViewOpenHandler = useCallback(
    (templateId) => {
      const template = findTemplateById(templateId);
      setTemplateToQuickView(template);
      setTemplateIdToQuickView(templateId);
      setQuickViewOpen(true);
    },
    [findTemplateById]
  );

  const onClickQuickViewCloseHandler = useCallback(() => {
    setQuickViewOpen(false);
    setHideQuickViewCtas(false);
  }, []);

  const onTemplateClick = useCallback(
    (e, templateId, href) => {
      e.preventDefault();
      e.stopPropagation();

      // Find template
      const template = findTemplateById(templateId);
      const isCard = template?.usedAs === 'card';
      const isQuickViewEnabled = checkIfQuickViewEnabled(template);

      if (
        !isCard &&
        isQuickViewEnabled &&
        ((!template.premium && isMediumAndUp) || !isMediumAndUp)
      ) {
        setTemplateIdToQuickView(templateId);
        setTemplateToQuickView(template);
        setQuickViewOpen(true);
        return;
      }

      window.location.href = href;
    },
    [findTemplateById, checkIfQuickViewEnabled, isMediumAndUp]
  );

  const onStartCustomizingClick = useCallback(
    (e, variant) => {
      const href = buildHref(templateToQuickView, tracking, currentPage);
      const url = new window.URL(`${window.location.origin}${href ?? ''}`);
      url.searchParams.append('variant', variant);
      if (variant === 'premium') {
        trackUpsell({
          path: window.location.pathname.split('/').at(0) || 'invites',
          feature: 'design',
          status: EVENT_STATUS_DRAFT,
        });
      }
      window.location.href = url.toString();
    },
    [currentPage, tracking, templateToQuickView]
  );

  const onPremiumInfoClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setHideQuickViewCtas(true);
    setTemplateIdToQuickView('disney-0147-liloandstitch-somewhere-sandy');
    setTemplateToQuickView(defaultTemplatePreviewConfig);
    setQuickViewOpen(true);
  }, []);

  if (window.is_favorites_page && (!favorites || favorites.length === 0)) {
    return favorites ? <NoFavorites /> : null;
  }
  // Don't render if results haven't come in yet to reduce CLS score
  if (!favorites?.length && !templates?.free?.length && !templates?.premium?.length) return null;

  return (
    <>
      <SeoHeaderWithBreadcrumb />

      <div
        className={cx('gallery__root__content', {
          'gallery__root__content__in-3-across-ABTest': in3AcrossABTest && showSeoHeader,
        })}
      >
        <Header
          crumbPath={breadcrumbs}
          pageTitle={pageDisplayName}
          numTemplates={replaced ? 0 : numTemplates}
          activeFilters={previewedFilters}
          onFilterDismiss={handleFilterClick}
          onClearAllFilters={resetFilters}
          categoryId={categoryId}
          {...(isLargishAndUp && {
            filtersData: filters,
            relatedCategories: relatives,
            galleryUploadComponent: isInUyoOrDyo ? <PreviewPhotoUpload /> : null,
          })}
        />
        {isLargishAndUp ? (
          <div
            className={`gallery__body ${urlPieces.includes('favorites') ? 'favorites-page' : ''}`}
          >
            <div>
              <GalleryResults
                freeTemplates={templates.free || []}
                premiumTemplates={templates.premium || []}
                favorites={favorites || []}
                showNoResultsHeader={replaced} // Controls if No Search Results header is displayed
                categoryId={categoryId}
                onTemplateClick={onTemplateClick}
                templatesPerRowOnDesktop={templatesPerRowOnDesktop}
                onClickQuickView={onClickQuickViewOpenHandler}
              />
              {pageCount > 1 && (
                <>
                  <Divider marginTop="2.5rem" marginBottom="2.5rem" />
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pageCount}
                    onPagePress={onPaginationPagePress}
                    onLeftArrowPress={onPaginationLeftArrowPress}
                    onRightArrowPress={onPaginationRightArrowPress}
                  />
                  <Divider marginTop="2rem" marginBottom="2rem" />
                </>
              )}
              <SEODescriptionBlock />
            </div>
          </div>
        ) : (
          <>
            {!urlPieces.includes('favorites') ? <SubCategories group={relatives} /> : null}

            <ResultsHeader
              onFilterButtonClick={onFilterButtonClick}
              numTemplates={replaced ? 0 : numTemplates}
              showFilterButton={
                Object.keys(filters).length > 0 && // There are filter groups defined
                Object.values(filters).some((f) => Object.keys(f.filters).length > 0) // Each group has filters
              }
            />
            <div className="gallery__mw-filter-previewer-wrapper">
              <FilterPreviewer
                filters={previewedFilters}
                onFilterDismiss={handleFilterClick}
                onClearAllFilters={resetFilters}
              />
            </div>
            {isInUyoOrDyo && (
              <div className="gallery__mw-preview-photo-upload__wrapper">
                <Suspense fallback={<div>Loading...</div>}>
                  <PreviewPhotoUpload />
                </Suspense>
              </div>
            )}
            <GalleryResults
              freeTemplates={templates.free}
              premiumTemplates={templates.premium}
              favorites={favorites || []}
              showNoResultsHeader={replaced} // Controls if No Search Results header is displayed
              categoryId={categoryId}
              onTemplateClick={onTemplateClick}
              templatesPerRowOnDesktop={templatesPerRowOnDesktop}
              onClickQuickView={onClickQuickViewOpenHandler}
            />
            {pageCount > 1 && (
              <>
                <Divider marginTop="2rem" marginBottom="2rem" />
                <Pagination
                  currentPage={currentPage}
                  totalPages={pageCount}
                  onPagePress={onPaginationPagePress}
                  onLeftArrowPress={onPaginationLeftArrowPress}
                  onRightArrowPress={onPaginationRightArrowPress}
                />
              </>
            )}
            <FiltersDrawer
              open={isDrawerOpen}
              handleCancelClick={handleCancelClick}
              handleApplyClick={handleApplyClick}
              filters={filters}
              onFilterToggle={handleFilterClick}
              onPremiumInfoClick={onPremiumInfoClick}
            />
            {pageCount > 1 && <Divider marginTop="2rem" marginBottom="2rem" />}
            <SEODescriptionBlock />
          </>
        )}
        <LoadingOverlay loadingMessage="Loading results" show={isWaitingForSearchResults} />
        <QuickView
          key={quickViewOpen}
          open={quickViewOpen}
          onClose={onClickQuickViewCloseHandler}
          templateId={templateIdToQuickView}
          template={templateToQuickView}
          onStartCustomizingClick={onStartCustomizingClick}
          openQuickView={onClickQuickViewOpenHandler}
          hideCtas={hideQuickViewCtas}
        />
        {createPortal(<LoginModalWrapper />, document.body)}
      </div>
    </>
  );
};
